// The update box, with an @ picker for the rest of the team.
//
// A separate file rather than more lines inside TaskPanel.tsx, for two reasons.
// The panel is already 20kB and is being edited by other work; and the matching
// rules this depends on are pure functions in utils/tasks.ts with their own
// tests, so the component here is only keyboard and caret handling.
//
// WHAT THIS DELIBERATELY IS NOT
//
// It is not a rich text editor. The body stays plain text, exactly as it is
// stored and exactly as the email renders it, and a mention is the literal
// characters "@Kabelo Seboka" sitting in that text. That is why deleting the
// words removes the mention with no extra bookkeeping: see mentionIdsInBody.
// A contenteditable with embedded tokens would look neater and would introduce
// a second representation of the same sentence, which is how a comment ends up
// displaying something different from what was emailed.
//
// No dialogs, no alert(), no confirm(): a modal dialog inside a panel that is
// itself dismissible on outside-click is how you strand somebody.
import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '../../lib/shadcn/utils'
import { Textarea } from '../../lib/shadcn/textarea'
import { bubbleClass, initials, mentionQueryAt, type TaskPerson } from '../../utils/tasks'

const MAX_SUGGESTIONS = 6

export function MentionTextarea({
  value,
  onChange,
  people,
  placeholder,
  className,
  disabled,
}: {
  value: string
  onChange: (next: string) => void
  people: TaskPerson[]
  placeholder?: string
  className?: string
  disabled?: boolean
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null)
  const [caret, setCaret] = useState(0)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)

  // Only people with a record behind them can be mentioned. An assignee name
  // with nobody behind it is a real thing on this board and there is nobody to
  // notify, so offering it would promise something the system cannot do.
  const mentionable = useMemo(
    () => people.filter((p) => p.person_id && p.name.trim().length > 0),
    [people],
  )

  const hit = open ? mentionQueryAt(value, caret) : null
  const matches = useMemo(() => {
    if (!hit) return []
    const q = hit.query.trim().toLowerCase()
    const pool = q
      ? mentionable.filter((p) => p.name.toLowerCase().includes(q))
      : mentionable
    return pool.slice(0, MAX_SUGGESTIONS)
  }, [hit, mentionable])

  // Reset the highlight whenever the candidate list changes, so Enter never
  // lands on whoever happened to be selected for a different query.
  useEffect(() => {
    setActive(0)
  }, [hit?.query])

  const showing = hit !== null && matches.length > 0

  const choose = (person: TaskPerson) => {
    if (!hit) return
    const before = value.slice(0, hit.start)
    const after = value.slice(caret)
    // The trailing space matters: without it the next character typed extends
    // the name and mentionIdsInBody stops matching it, so the mention silently
    // disappears at post time.
    const inserted = `@${person.name} `
    const next = before + inserted + after
    onChange(next)
    setOpen(false)
    const pos = before.length + inserted.length
    // After React has written the new value, or the caret jumps to the end.
    requestAnimationFrame(() => {
      const el = ref.current
      if (!el) return
      el.focus()
      el.setSelectionRange(pos, pos)
      setCaret(pos)
    })
  }

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        className={cn('min-h-[72px] text-sm', className)}
        onChange={(e) => {
          onChange(e.target.value)
          setCaret(e.target.selectionStart ?? e.target.value.length)
          setOpen(true)
        }}
        onClick={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onKeyUp={(e) => {
          // Arrow keys and Home/End move the caret without changing the value,
          // and the picker has to follow the caret or it offers names for a
          // mention the person has navigated away from.
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
          setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)
        }}
        onKeyDown={(e) => {
          if (!showing) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setActive((i) => (i + 1) % matches.length)
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => (i - 1 + matches.length) % matches.length)
          } else if (e.key === 'Enter' || e.key === 'Tab') {
            // Enter picks the name rather than posting. Posting is the button,
            // deliberately: Enter-to-send in a box that also completes names
            // means one keystroke either finishes a word or publishes a comment
            // depending on state nobody can see.
            e.preventDefault()
            const person = matches[active]
            if (person) choose(person)
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setOpen(false)
          }
        }}
        onBlur={() => {
          // Late enough for a click on a suggestion to land first.
          setTimeout(() => setOpen(false), 120)
        }}
      />

      {showing && (
        <ul
          role="listbox"
          aria-label="People you can mention"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {matches.map((p, i) => (
            <li key={p.person_id as string}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                // mousedown, not click: the textarea's blur fires first on click
                // and closes the list before the selection is read.
                onMouseDown={(e) => {
                  e.preventDefault()
                  choose(p)
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm',
                  i === active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                )}
              >
                <span
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold text-white',
                    bubbleClass(p.name),
                  )}
                >
                  {initials(p.name)}
                </span>
                <span className="truncate">{p.name}</span>
                {!p.has_login && (
                  // Says so rather than hiding them: they can be mentioned and
                  // the record is real, but nobody should expect them to read it
                  // on the board tonight.
                  <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                    no login
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
